import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { updateSetupTheme, UpdateSetupThemeConfig } from "../rest/themeSetup";
import { APPID } from "@/utils/domain";
import { CachedQueryKeys } from "./cachedQueryKeys";
import { ThemeDraft } from "@/theme";

const getThemeQueryCacheKey = (appId: string) => {
  const themeCacheId = ThemeDraft.hasSetupId()
    ? `setup-${ThemeDraft.getCurrentId("setup")}`
    : appId;
  return [CachedQueryKeys.AppTheme, themeCacheId];
};

export const themeSetupQueryOptions = (appId: string = APPID) => {
  return queryOptions({
    queryKey: getThemeQueryCacheKey(appId),
    queryFn: async ({ signal }) => {
      if (!appId) {
        throw new Error("App ID is required to fetch theme setup");
      }
      return ThemeDraft.getSetupTheme(appId, signal);
    },
    // Use immediate updates
    staleTime: 0,
  });
};

export const useUpdateSetupTheme = (
  onSuccess?: (data: Awaited<ReturnType<typeof updateSetupTheme>>) => void,
  onError?: (error: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: UpdateSetupThemeConfig) => {
      return updateSetupTheme(params);
    },
    onError: (error) => {
      console.error(error);
      onError?.(error);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getThemeQueryCacheKey(variables.appId),
      });
      onSuccess?.(data);
    },
  });
};
